import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useProtocol, useProtocolBooking } from "@/hooks/useProtocol";
import {
  SignaturePad,
  type SignaturePadHandle,
} from "@/components/protocol/SignaturePad";
import { formatDateDE } from "@/lib/protocol";
import type { ProtocolCopy } from "@/lib/protocol-copy";

export type SignStepHandle = {
  isEmpty: () => boolean;
  toDataURL: () => string;
};

export const SignStep = forwardRef<
  SignStepHandle,
  {
    protocolId: string;
    t: ProtocolCopy;
    onInkChange?: (hasInk: boolean) => void;
  }
>(function SignStep({ protocolId, t, onInkChange }, ref) {
  const { user } = useAuth();
  const { data: protocol } = useProtocol(protocolId);
  const { data: booking } = useProtocolBooking(protocol?.booking_id ?? null);
  const padRef = useRef<SignaturePadHandle | null>(null);
  const [, force] = useState(0);

  useImperativeHandle(
    ref,
    () => ({
      isEmpty: () => padRef.current?.isEmpty() ?? true,
      toDataURL: () => padRef.current?.toDataURL() ?? "",
    }),
    []
  );

  if (!protocol) return null;

  const displayName =
    (user?.user_metadata?.name as string | undefined) ||
    user?.email?.split("@")[0] ||
    t.tenant;

  return (
    <div>
      <h2 className="text-[26px] font-bold leading-tight text-neutral-900 md:text-3xl">
        {t.signTitle}
      </h2>
      <p className="mt-1 text-sm text-neutral-500 md:max-w-2xl">
        {t.signSubtitle}
      </p>

      <div className="mt-5 grid grid-cols-1 gap-5 md:mt-8 md:grid-cols-2 md:gap-8">
        <section>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-neutral-400">
            {t.summary}
          </div>
          <div className="mt-2 overflow-hidden rounded-2xl border border-neutral-200 bg-white">
            <Row label={t.object} value={booking?.property_name ?? "—"} />
            <Row
              label={t.roomLabel}
              value={
                booking?.room_number ? `${t.room} ${booking.room_number}` : "—"
              }
            />
            <Row label={t.date} value={formatDateDE(new Date())} />
            <Row label={t.tenant} value={displayName} />
            <Row
              label={t.operation}
              value={protocol.kind === "move_in" ? t.moveIn : t.moveOut}
              last
            />
          </div>
        </section>

        <section>
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-neutral-400">
              {t.signature}
            </span>
            <button
              type="button"
              onClick={() => {
                padRef.current?.clear();
                force((v) => v + 1);
              }}
              className="text-sm font-medium text-emerald-600"
            >
              {t.clear}
            </button>
          </div>
          <SignaturePad
            ref={padRef}
            className="mt-2"
            height={260}
            onInkChange={onInkChange}
          />
          <p className="mt-3 text-xs leading-relaxed text-neutral-500">
            {t.signTerms}
          </p>
        </section>
      </div>
    </div>
  );
});

function Row({
  label,
  value,
  last,
}: {
  label: string;
  value: string;
  last?: boolean;
}) {
  return (
    <div
      className={`flex items-center justify-between px-4 py-3 ${last ? "" : "border-b border-neutral-100"}`}
    >
      <span className="text-sm text-neutral-500">{label}</span>
      <span className="text-sm font-semibold text-neutral-900">{value}</span>
    </div>
  );
}
